import {
  ValueDescription_T,
  type InputConfig,
  type InputDescription,
  type LimitSort,
  type ValueConfig,
  type ValueData,
} from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import {
  ActionLabel,
  INPUT_ACTION_NAME_ID,
  INPUT_AI_ACTION_NAME_ID,
} from "./constants";

interface InputType<T> {
  inputId: string;
  value?: T;
}

export const findInputValue = <T>(
  inputs: Array<InputType<T>>,
  inputId: string,
): T | undefined => {
  for (const input of inputs) {
    if (input.inputId === inputId) {
      return input.value;
    }
  }

  return undefined;
};

export const pickValueConfigString = (
  value: ValueConfig,
): string | undefined => {
  if (value.values[0]?.value.oneofKind === "string") {
    return value.values[0].value.string;
  }
  return undefined;
};

export const pickValueConfigStringArray = (value: ValueConfig): string[] => {
  return value.values
    .map((value) => {
      if (value.value.oneofKind === "string") {
        return value.value.string;
      }
      return "";
    })
    .filter((value) => R.isTruthy(value));
};

export const pickMemberIdFromMemberEmail = (value: ValueConfig): string[] => {
  return value.values
    .map((value) => {
      if (value.value.oneofKind === "function") {
        if (value.value.function.function.oneofKind === "memberEmail") {
          return value.value.function.function.memberEmail.memberId;
        }
      }
      return "";
    })
    .filter((value) => R.isTruthy(value));
};

export const pickTeamIdFromTeamEmails = (value: ValueConfig): string[] => {
  return value.values
    .map((value) => {
      if (value.value.oneofKind === "function") {
        if (value.value.function.function.oneofKind === "teamEmails") {
          return value.value.function.function.teamEmails.teamId;
        }
      }
      return "";
    })
    .filter((value) => R.isTruthy(value));
};

export const pickOutputIdFromReference = (value: ValueConfig): string[] => {
  return value.values
    .map((value) => {
      if (value.value.oneofKind === "reference") {
        return value.value.reference.outputId;
      }
      return "";
    })
    .filter((value) => R.isTruthy(value));
};

export const pickValueDataString = (
  value: ValueData | ValueConfig,
): string | undefined => {
  switch (value.values[0]?.value.oneofKind) {
    case "string":
      return value.values[0]?.value.string;
    default:
      return undefined;
  }
};

export const findInputAndPick = <T, R>(
  inputs: Array<InputType<T>>,
  inputId: string,
  picker: (v: T) => R | undefined,
): R | undefined => {
  const found = findInputValue(inputs, inputId);
  if (found === undefined) {
    return undefined;
  }
  return picker(found);
};

// The AI action declares its "Action Name" field under a dedicated
// `action_name` (underscore) input, whereas every other action uses the
// standard `action-name` (hyphen) input. Older AI actions may still carry a
// stale `action-name` value from a previous label, so we must read the input
// that the action's own description actually uses rather than blindly falling
// back to the legacy one.
export const getActionNameInputId = (actionId?: string): string =>
  actionId === ActionLabel.ai ? INPUT_AI_ACTION_NAME_ID : INPUT_ACTION_NAME_ID;

// Resolves the user-facing "Action Name" value for an action. Returns undefined
// when the user has not set a name, letting callers fall back to the action's
// default label.
export const pickActionName = (
  inputs: Array<InputType<ValueConfig>>,
  actionId?: string,
): string | undefined =>
  findInputAndPick(
    inputs,
    getActionNameInputId(actionId),
    pickValueConfigString,
  );

export const pickValueConfigLimitSort = (
  value: ValueConfig,
): LimitSort | undefined => {
  if (value.values[0]?.value.oneofKind === "limitSort") {
    return value.values[0].value.limitSort;
  }
  return undefined;
};

export const buildStringInputConfig = (
  inputId: string,
  value: string,
): InputConfig => ({
  valueDescription: {
    type: ValueDescription_T.STRING,
    multiple: false,
  },
  inputId,
  value: {
    values: [
      {
        value: {
          oneofKind: "string",
          string: value,
        },
      },
    ],
  },
});

// An input the builder renders a control for. A description can hide an input
// two ways -- no editor, or a hidden display -- and an integration that keeps
// an input only so saved values are not shown as unknown fields declares it
// with a hidden editor.
export const hasVisibleEditor = (input: InputDescription): boolean => {
  const editorKind = input.editor?.editor.oneofKind;
  return (
    editorKind !== undefined &&
    editorKind !== "hidden" &&
    input.display?.display.oneofKind !== "hidden"
  );
};
